import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { AppService } from '../../../services/app.service';
import { AuthService } from '../../../services/auth.service';
import {
  FeeDisplay,
  TasadorCoin,
  TasadorQuoteResult,
  TasadorService,
} from '../../../services/tasador.service';
import { UserService } from '../../../services/users.service';
import { Roles } from '../../../models/roles';

export interface ExchangeRateCalculatorPair {
  source: string;
  target: string;
}

@Component({
  selector: 'app-exchange-rate-calculator',
  templateUrl: './exchange-rate-calculator.component.html',
  styleUrl: './exchange-rate-calculator.component.scss',
})
export class ExchangeRateCalculatorComponent
  implements OnInit, OnChanges, OnDestroy
{
  private readonly FIXED_FEE_PERCENT = 0.001; // 0.1%
  private readonly DEFAULT_QUOTE_TTL_SECONDS = 15;
  private readonly PROVIDER_NAME_PATTERN =
    /\b(BITSO|Bitso|bitso|VELAFI|VelaFi|Velafi|velafi|Depasify|DEPASIFY|depasify|Depa|DEPA|depa)\b/g;
  private rateExpirationTimer: ReturnType<typeof setInterval> | null = null;
  private rateExpirationTotalMs = 0;
  rateExpirationRemainingMs = 0;
  visible = true;
  loadingCoins = false;
  loadingQuote = false;
  creatingExchange = false;
  coins: TasadorCoin[] = [];
  /** Pares (origen → destino) con cotización disponible */
  supportedPairs: ExchangeRateCalculatorPair[] = [];
  /** Por cada moneda origen, set de monedas destino con cotización */
  targetsBySource: Record<string, Set<string>> = {};
  quoteResult: TasadorQuoteResult | null = null;
  quoteError: string | null = null;
  @Input() initialCurrency = '';

  quoteForm = this.formBuilder.group({
    source_currency: [null as string | null, Validators.required],
    target_currency: [null as string | null, Validators.required],
    amount_send: [null as number | null],
  });

  constructor(
    private tasadorService: TasadorService,
    private formBuilder: FormBuilder,
    public appService: AppService,
    private auth: AuthService,
    private userService: UserService,
    private messageService: MessageService,
  ) {}

  ngOnInit(): void {
    this.applyInitialCurrency();
    this.loadCoinsAndPairs();
    this.setupOriginDestinationSync();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['initialCurrency'] && !changes['initialCurrency'].firstChange) {
      this.applyInitialCurrency();
    }
  }

  ngOnDestroy(): void {
    this.stopRateExpirationTimer();
  }

  private applyInitialCurrency(): void {
    const currency = String(this.initialCurrency || '').trim().toUpperCase();
    if (!currency) return;

    this.quoteForm.patchValue(
      {
        source_currency: currency,
        target_currency:
          this.quoteForm.controls.target_currency.value === currency
            ? null
            : this.quoteForm.controls.target_currency.value,
      },
      { emitEvent: false },
    );
    this.clearResult();
  }

  private setupOriginDestinationSync(): void {
    this.quoteForm.controls.source_currency.valueChanges.subscribe((source) => {
      const target = this.quoteForm.controls.target_currency.value;
      if (source && target && source === target) {
        this.quoteForm.controls.target_currency.setValue(null, {
          emitEvent: false,
        });
      }
    });
    this.quoteForm.controls.target_currency.valueChanges.subscribe((target) => {
      const source = this.quoteForm.controls.source_currency.value;
      if (source && target && source === target) {
        this.quoteForm.controls.source_currency.setValue(null, {
          emitEvent: false,
        });
      }
    });
  }

  loadCoinsAndPairs(): void {
    this.loadingCoins = true;
    Promise.all([
      this.tasadorService.getCoins(),
      this.tasadorService.getPairs(),
    ])
      .then(([coinsResp, pairsResp]) => {
        this.coins = coinsResp.data || [];
        this.supportedPairs = pairsResp.data || [];
        this.targetsBySource = this.buildTargetsBySource(this.supportedPairs);
        this.loadingCoins = false;
      })
      .catch((err) => {
        this.loadingCoins = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: this.sanitizeVisibleError(
            err?.message,
            'No se pudieron cargar monedas o pares.',
          ),
        });
      });
  }

  closeCalculatorModal(): void {
    this.stopRateExpirationTimer();
    this.visible = false;
    this.appService.triggerExchangeRateCalculator(false);
  }

  private buildTargetsBySource(
    pairs: ExchangeRateCalculatorPair[],
  ): Record<string, Set<string>> {
    const map: Record<string, Set<string>> = {};
    for (const { source, target } of pairs) {
      if (!map[source]) map[source] = new Set();
      map[source].add(target);
    }
    return map;
  }

  /** Opciones para moneda origen: todas las que tienen al menos un par, excluyendo la actual destino */
  getSourceOptions(): { label: string; value: string }[] {
    const target = this.quoteForm.controls.target_currency.value;
    const hasPairs = this.supportedPairs.length > 0;
    const sourceIds = hasPairs
      ? [...new Set(this.supportedPairs.map((p) => p.source))]
      : this.coins.map((c) => c.id);
    return this.coins
      .filter((c) => sourceIds.includes(c.id) && c.id !== target)
      .map((c) => ({
        label: `${c.shortname || c.id} - ${c.name || c.id}`,
        value: c.id,
      }));
  }

  /** Opciones para moneda destino: solo pares soportados con el origen elegido, excluyendo origen */
  getTargetOptions(): { label: string; value: string }[] {
    const source = this.quoteForm.controls.source_currency.value;
    const allowedTargets =
      source && this.targetsBySource[source]
        ? Array.from(this.targetsBySource[source])
        : this.coins.map((c) => c.id);
    return this.coins
      .filter((c) => c.id !== source && allowedTargets.includes(c.id))
      .map((c) => ({
        label: `${c.shortname || c.id} - ${c.name || c.id}`,
        value: c.id,
      }));
  }

  onSubmit(): void {
    if (this.quoteForm.invalid || this.loadingQuote || this.creatingExchange)
      return;

    if (!this.canQuoteExchange) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Permiso requerido',
        detail: 'No tienes permisos para cotizar exchange.',
      });
      return;
    }

    const src = this.quoteForm.controls.source_currency.value;
    const tgt = this.quoteForm.controls.target_currency.value;
    if (!src || !tgt || src === tgt) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Datos incompletos',
        detail: 'Elige moneda origen y destino distintas.',
      });
      return;
    }
    if (this.hasInsufficientSourceBalance) {
      this.quoteError = this.sourceBalanceErrorMessage;
      this.messageService.add({
        severity: 'warn',
        summary: 'Saldo insuficiente',
        detail: this.sourceBalanceErrorMessage,
      });
      return;
    }

    this.quoteError = null;
    this.quoteResult = null;
    this.stopRateExpirationTimer();
    this.loadingQuote = true;

    const amount = this.quoteForm.controls.amount_send.value;

    this.tasadorService
      .getQuote({
        source_currency: src,
        target_currency: tgt,
        amount_send: amount != null && amount > 0 ? amount : undefined,
      })
      .then(
        (resp) => {
          this.quoteResult = resp.data;
          try {
            this.startRateExpirationTimer(resp.data);
          } finally {
            this.loadingQuote = false;
          }
        },
        (err) => {
          this.loadingQuote = false;
          this.stopRateExpirationTimer();
          const body = err?.error ?? err;
          this.quoteError = this.sanitizeVisibleError(
            body?.message,
            'No se pudo obtener la cotización.',
          );
          if (body?.data?.valid_currencies?.length) {
            this.quoteError += ` Monedas válidas: ${body.data.valid_currencies.join(', ')}.`;
          }
          if (body?.data?.supported_quote_pairs?.length) {
            this.quoteError += ` Pares con cotización: ${body.data.supported_quote_pairs.join(', ')}.`;
          }
        },
      );
  }

  private calculateFixedFee(amount: number): number {
    return amount * this.FIXED_FEE_PERCENT;
  }

  get quoteAmountSend(): number | null {
    const formAmount = this.quoteForm.controls.amount_send.value;
    if (formAmount != null && formAmount > 0) return formAmount;
    if (
      this.quoteResult?.amount_send != null &&
      this.quoteResult.amount_send > 0
    )
      return this.quoteResult.amount_send;
    return null;
  }

  get fixedFeeAmount(): number | null {
    if (this.quoteResult?.fee != null) return this.quoteResult.fee;
    return null;
  }

  get subtotalAmount(): number | null {
    if (this.quoteResult?.amount_net != null) return this.quoteResult.amount_net;
    const amount = this.quoteAmountSend;
    const fee = this.fixedFeeAmount;
    if (amount == null || fee == null) return null;
    return amount - fee;
  }

  get feePercentLabel(): string {
    return '0.1%';
  }

  clearResult(): void {
    this.quoteResult = null;
    this.quoteError = null;
    this.stopRateExpirationTimer();
  }

  get hasValidQuoteForExchange(): boolean {
    return (
      !!this.quoteResult &&
      !this.quoteError &&
      this.quoteResult.amount_send != null &&
      this.quoteResult.amount_send > 0 &&
      this.calculatedRecipientAmount != null &&
      this.calculatedRecipientAmount > 0
    );
  }

  get canCreateExchange(): boolean {
    return (
      this.hasValidQuoteForExchange &&
      this.quoteResult?.consulted_rate_id != null &&
      !this.isQuoteExpired &&
      !this.hasInsufficientSourceBalance &&
      this.canExecuteExchange
    );
  }

  get createExchangeDisabledTooltip(): string {
    if (!this.hasValidQuoteForExchange || this.canCreateExchange) return '';
    if (!this.canExecuteExchange) {
      return 'No tienes permisos para crear exchange';
    }
    if (this.hasInsufficientSourceBalance) {
      return this.sourceBalanceErrorMessage;
    }
    if (this.isQuoteExpired) {
      return 'La cotización expiró. Refresca la tasa';
    }
    if (this.quoteResult?.consulted_rate_id == null) {
      return 'La cotización no está lista para crear el exchange';
    }
    return '';
  }

  get sourceAvailableBalance(): number {
    const source = this.quoteForm.controls.source_currency.value;
    if (!source) return 0;
    const currency = this.userService.user_balance_config?.currencies?.find(
      (item: any) =>
        this.normalizeCurrency(item?.id) === this.normalizeCurrency(source) ||
        this.normalizeCurrency(item?.currency) ===
          this.normalizeCurrency(source),
    );
    return Number(currency?.balance?.total ?? 0) || 0;
  }

  get sourceAmount(): number {
    const amount = this.quoteForm.controls.amount_send.value;
    return Number(amount ?? 0) || 0;
  }

  get hasInsufficientSourceBalance(): boolean {
    if (this.auth.hasRole(Roles.Admin)) return false;
    const source = this.quoteForm.controls.source_currency.value;
    if (!source) return false;
    return (
      this.sourceAmount > 0 && this.sourceAmount > this.sourceAvailableBalance
    );
  }

  get sourceBalanceErrorMessage(): string {
    const source = this.quoteForm.controls.source_currency.value || '';
    return `Saldo insuficiente. Disponible: ${this.formatBalance(this.sourceAvailableBalance)} ${source}.`;
  }

  private normalizeCurrency(value: any): string {
    return String(value ?? '').trim().toUpperCase();
  }

  private formatBalance(value: number): string {
    return value.toLocaleString('es', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 8,
    });
  }

  private sanitizeVisibleError(message: any, fallback: string): string {
    const raw = String(message || fallback || '').trim();
    const sanitized = raw
      .replace(this.PROVIDER_NAME_PATTERN, '')
      .replace(/\s{2,}/g, ' ')
      .replace(/\s+([:.,;])/g, '$1')
      .trim();
    return sanitized || fallback;
  }

  get isQuoteExpired(): boolean {
    return (
      this.quoteResult != null &&
      this.rateExpirationTotalMs > 0 &&
      this.rateExpirationRemainingMs <= 0
    );
  }

  get showRateExpirationBar(): boolean {
    return (
      this.hasValidQuoteForExchange &&
      this.quoteResult != null &&
      this.rateExpirationTotalMs > 0
    );
  }

  get rateExpirationProgressPercent(): number {
    if (this.rateExpirationTotalMs <= 0) return 0;
    const percent =
      (this.rateExpirationRemainingMs / this.rateExpirationTotalMs) * 100;
    return Math.max(0, Math.min(100, percent));
  }

  get rateExpirationSecondsLeft(): number {
    return Math.max(0, Math.ceil(this.rateExpirationRemainingMs / 1000));
  }

  private startRateExpirationTimer(quote: TasadorQuoteResult): void {
    this.stopRateExpirationTimer();
    const expiration = this.resolveQuoteExpiration(quote);
    if (!expiration) return;

    const { expiresAt, totalMs } = expiration;
    this.rateExpirationTotalMs = totalMs;
    this.rateExpirationRemainingMs = Math.max(0, expiresAt - Date.now());

    this.rateExpirationTimer = setInterval(() => {
      this.rateExpirationRemainingMs = Math.max(0, expiresAt - Date.now());
      if (this.rateExpirationRemainingMs <= 0) {
        this.stopRateExpirationTimer(false);
      }
    }, 250);
  }

  private resolveQuoteExpiration(
    quote: TasadorQuoteResult,
  ): { expiresAt: number; totalMs: number } | null {
    const providerExpiresAt = this.parseDateMs(quote.provider_quote_expires_at);
    const providerTtlSeconds = Number(quote.provider_quote_ttl_seconds || 0);
    if (providerExpiresAt != null) {
      const providerCreatedAt = this.parseDateMs(quote.provider_quote_created_at);
      const totalMs =
        providerTtlSeconds > 0
          ? providerTtlSeconds * 1000
          : quote.provider_quote_id == null && providerCreatedAt != null
            ? providerExpiresAt - providerCreatedAt
            : this.DEFAULT_QUOTE_TTL_SECONDS * 1000;
      return {
        expiresAt:
          providerTtlSeconds > 0 || quote.provider_quote_id == null
            ? providerExpiresAt
            : Date.now() + this.DEFAULT_QUOTE_TTL_SECONDS * 1000,
        totalMs: Math.max(0, totalMs),
      };
    }

    if (quote.provider_quote_id != null) {
      const seconds =
        providerTtlSeconds > 0
          ? providerTtlSeconds
          : this.DEFAULT_QUOTE_TTL_SECONDS;
      return {
        expiresAt: Date.now() + seconds * 1000,
        totalMs: seconds * 1000,
      };
    }

    const consultedExpiresAt = this.parseDateMs(quote.consulted_rate_expiration);
    const consultedTtlSeconds = Number(
      quote.consulted_rate_expires_in_seconds || 0,
    );
    if (consultedExpiresAt != null) {
      const totalMs =
        consultedTtlSeconds > 0
          ? consultedTtlSeconds * 1000
          : this.DEFAULT_QUOTE_TTL_SECONDS * 1000;
      return {
        expiresAt:
          consultedTtlSeconds > 0
            ? consultedExpiresAt
            : Date.now() + this.DEFAULT_QUOTE_TTL_SECONDS * 1000,
        totalMs: Math.max(0, totalMs),
      };
    }
    if (quote.consulted_rate_id != null) {
      const seconds =
        consultedTtlSeconds > 0
          ? consultedTtlSeconds
          : this.DEFAULT_QUOTE_TTL_SECONDS;
      return {
        expiresAt: Date.now() + seconds * 1000,
        totalMs: seconds * 1000,
      };
    }

    return null;
  }

  private parseDateMs(
    value: string | number | null | undefined,
  ): number | null {
    if (!value) return null;
    if (typeof value === 'number') {
      return value > 9999999999 ? value : value * 1000;
    }
    const numericValue = Number(value);
    if (Number.isFinite(numericValue) && String(value).trim() !== '') {
      return numericValue > 9999999999 ? numericValue : numericValue * 1000;
    }
    const normalized = value.includes(' ') ? value.replace(' ', 'T') : value;
    const parsed = Date.parse(normalized);
    return Number.isFinite(parsed) ? parsed : null;
  }

  private stopRateExpirationTimer(reset = true): void {
    if (this.rateExpirationTimer) {
      clearInterval(this.rateExpirationTimer);
      this.rateExpirationTimer = null;
    }
    if (reset) {
      this.rateExpirationTotalMs = 0;
      this.rateExpirationRemainingMs = 0;
    }
  }

  createExchange(): void {
    if (
      !this.canCreateExchange ||
      !this.quoteResult ||
      this.calculatedRecipientAmount == null ||
      this.creatingExchange ||
      this.loadingQuote
    )
      return;

    if (!this.canExecuteExchange) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Permiso requerido',
        detail: 'No tienes permisos para crear exchange.',
      });
      return;
    }

    if (this.quoteResult.consulted_rate_id == null) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'La cotización no está lista para crear el exchange.',
      });
      return;
    }

    this.creatingExchange = true;
    this.tasadorService
      .createExchangeFromQuote({
        consulted_rate_id: this.quoteResult.consulted_rate_id,
      })
      .then(
        () => {
          this.creatingExchange = false;
          this.messageService.add({
            severity: 'success',
            summary: 'Listo',
            detail: 'Exchange creado exitosamente.',
          });
          this.closeCalculatorModal();
        },
        (err) => {
          this.creatingExchange = false;
          const body = err?.error ?? err;
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: this.sanitizeVisibleError(
              body?.message,
              'Ocurrió un error al crear el exchange.',
            ),
          });
        },
      );
  }

  /** Intercambia moneda de envío y de recepción */
  swapCurrencies(): void {
    const source = this.quoteForm.controls.source_currency.value;
    const target = this.quoteForm.controls.target_currency.value;
    if (source && target) {
      this.quoteForm.controls.source_currency.setValue(target, {
        emitEvent: false,
      });
      this.quoteForm.controls.target_currency.setValue(source, {
        emitEvent: false,
      });
      this.clearResult();
    }
  }

  /** Texto del monto que recibe el destinatario (para mostrar en bloque "Recibe") */
  get displayRecipientAmount(): string | null {
    if (!this.quoteResult || this.quoteError) return null;
    const gets = this.calculatedRecipientAmount;
    if (gets == null) return null;
    const formatted =
      typeof gets === 'number'
        ? gets.toLocaleString('es', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 8,
          })
        : String(gets);
    return `${formatted} ${this.quoteResult.target_currency}`;
  }

  private get calculatedRecipientAmount(): number | null {
    if (!this.quoteResult) return null;
    if (this.quoteResult.amount_net != null && this.quoteResult.rate != null) {
      return this.quoteResult.amount_net * this.quoteResult.rate;
    }
    if (this.quoteResult.recipient_gets != null) {
      return this.quoteResult.recipient_gets;
    }
    return null;
  }

  /** Indica si hay tarifa configurada para mostrar (on-ramp u off-ramp). */
  hasFeeConfig(fee: FeeDisplay | null | undefined): boolean {
    if (!fee) return false;
    return (
      fee.percentage_fee !== 0 || (fee.fixed_fee != null && fee.fixed_fee > 0)
    );
  }

  get canExecuteExchange(): boolean {
    return this.userService.hasPlatformService('exchange:execute');
  }

  get canQuoteExchange(): boolean {
    return this.userService.hasPlatformService([
      'exchange:quote',
      'exchange:execute',
    ]);
  }

  /** Formatea la tarifa (on-ramp u off-ramp) para mostrar: "0.5%" o "-0.25% + 0.5 EUR" */
  formatFeeDisplay(fee: FeeDisplay | null | undefined): string {
    if (!fee || (fee.percentage_fee === 0 && fee.fixed_fee === 0)) return '—';
    const parts: string[] = [];
    if (fee.percentage_fee !== 0) {
      parts.push(`${fee.percentage_fee > 0 ? '' : ''}${fee.percentage_fee}%`);
    }
    if (fee.fixed_fee != null && fee.fixed_fee > 0 && fee.fixed_fee_currency) {
      parts.push(
        `${fee.fixed_fee.toLocaleString('es', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${fee.fixed_fee_currency}`,
      );
    }
    return parts.join(' + ') || '—';
  }
}
